import LegalLayout, { LegalSection, SUPPORT_EMAIL } from "@/components/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="September 6, 2026">
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of Work &amp; Keep (the
        &ldquo;Service&rdquo;), operated by Wholehearted Wellness LLC (&ldquo;Work &amp; Keep,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us&rdquo;). By creating an account, purchasing, or using the Service, you agree to these Terms. If you do
        not agree, do not use the Service.
      </p>

      <LegalSection heading="The Service">
        <p>
          Work &amp; Keep is an online software service (SaaS) that provides a private workspace and tools for solo
          founders, including content, brand, projects, planning, knowledge, business tracking, and an AI Business Guide.
          The Service is provided on a continuous monthly subscription basis.
        </p>
      </LegalSection>

      <LegalSection heading="Accounts and eligibility">
        <ul className="list-disc pl-6 space-y-2">
          <li>You must provide accurate information and keep your account credentials secure.</li>
          <li>You are responsible for activity that occurs under your account.</li>
          <li>You must be at least 18 years old and able to enter into a binding agreement.</li>
          <li>Authentication is provided through Firebase (a Google service).</li>
        </ul>
      </LegalSection>

      <LegalSection heading="Connected email accounts">
        <ul className="list-disc pl-6 space-y-2">
          <li>The Service includes an optional Email feature that lets you connect an email account to retrieve messages and turn them into CRM records, tasks, and follow-ups. It is read-only in this phase and does not send email on your behalf.</li>
          <li>You represent that you own, or are authorized to access, any email account you connect, and you authorize us to access it on your behalf solely to provide this feature.</li>
          <li>You are responsible for the credentials you provide and for keeping them accurate. You may disconnect a connected account at any time, which removes its stored credentials and synced email data.</li>
          <li>Connecting an account through a third-party email provider is also subject to that provider&rsquo;s own terms.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="Billing, fees, and recurring subscription">
        <p>Pricing for the Service is:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>A one-time setup and onboarding fee of <strong>$149</strong>.</li>
          <li>A recurring subscription of <strong>$70 per month</strong>.</li>
          <li>
            An initial charge of <strong>$219</strong> at signup, which includes the $149 setup fee and your first $70
            monthly subscription payment.
          </li>
        </ul>
        <p>
          By subscribing, you authorize us and our payment processor (Stripe) to charge <strong>$70 per month on a
          recurring basis</strong> to your payment method until you cancel. Your subscription automatically renews each
          month until canceled. Applicable taxes may be added at checkout. If a payment fails, we may retry the charge
          or suspend access until payment is resolved.
        </p>
      </LegalSection>

      <LegalSection heading="Refund policy">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Setup fee.</strong> The $149 setup fee is refundable only before setup work begins. Setup begins
            once account configuration, onboarding preparation, workspace setup, customization, data organization, or
            other implementation work begins.
          </li>
          <li>
            <strong>First monthly payment.</strong> The first $70 monthly subscription payment may be refunded if
            requested within 3 days of the initial subscription charge.
          </li>
          <li>
            <strong>After the 3-day period.</strong> After that 3-day period, subscription payments are nonrefundable,
            and no prorated refunds are provided for unused time, except where required by law.
          </li>
          <li>
            <strong>Renewal charges.</strong> Renewal charges are nonrefundable except for billing errors, duplicate
            charges, or where required by law.
          </li>
        </ul>
        <p>
          To request a refund where eligible, contact us at {" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary underline">{SUPPORT_EMAIL}</a>.
        </p>
      </LegalSection>

      <LegalSection heading="Cancellation">
        <p>
          You may cancel at any time through the Stripe customer portal, which you can reach from your account settings
          (&ldquo;Manage subscription&rdquo;). Cancellation takes effect at the end of your current paid billing period,
          and your access continues through that period. To avoid the next recurring charge, cancel before your next
          billing date.
        </p>
      </LegalSection>

      <LegalSection heading="Your content and license">
        <p>
          You retain ownership of the information and files you add to your workspace (&ldquo;Your Content&rdquo;). You
          grant us a limited license to host, store, process, and display Your Content solely to operate and provide the
          Service to you, including sending prompts and context you submit to the AI Business Guide to our model provider
          to generate responses. You are responsible for Your Content and for having the rights to use it.
        </p>
      </LegalSection>

      <LegalSection heading="Intellectual property">
        <p>
          The Service, including its software, design, and content we provide, is owned by us or our licensors and is
          protected by intellectual property laws. These Terms do not grant you any right to our trademarks, branding,
          or software except the limited right to use the Service as intended.
        </p>
      </LegalSection>

      <LegalSection heading="Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Use the Service for unlawful purposes or to store or share unlawful content.</li>
          <li>Attempt to access other customers&rsquo; workspaces or data.</li>
          <li>Connect an email account you do not own or are not authorized to access.</li>
          <li>Interfere with, disrupt, or attempt to gain unauthorized access to the Service.</li>
          <li>Reverse engineer, resell, or copy the Service except as permitted by law.</li>
          <li>Misuse the Service in a way that harms us or other users.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="Third-party services">
        <p>
          The Service relies on third-party providers, including Stripe (payments), Firebase/Google (authentication),
          our AI model provider, PostHog (analytics), Cloudflare (delivery and security), and, if you connect an inbox,
          your chosen email provider (for example an IMAP/SMTP host such as Namecheap Private Email; additional email
          providers may be supported over time). Your use of those features may also be subject to the providers&rsquo; own terms.
        </p>
      </LegalSection>

      <LegalSection heading="AI output disclaimer">
        <p>
          The AI Business Guide produces suggestions and drafts for informational purposes based on the context you
          provide. It is not professional, legal, financial, tax, or medical advice, and it may be incomplete or
          inaccurate. You are responsible for reviewing and deciding how to use its output.
        </p>
      </LegalSection>

      <LegalSection heading="Disclaimers">
        <p>
          The Service is provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; without warranties of any kind,
          whether express or implied, to the fullest extent permitted by law. We do not warrant that the Service will be
          uninterrupted, error-free, or that it will meet your specific requirements.
        </p>
      </LegalSection>

      <LegalSection heading="Limitation of liability">
        <p>
          To the fullest extent permitted by law, Work &amp; Keep and Wholehearted Wellness LLC will not be liable for
          any indirect, incidental, special, consequential, or punitive damages, or for lost profits or data. Our total
          liability for any claim relating to the Service will not exceed the amount you paid us for the Service in the
          twelve (12) months before the event giving rise to the claim.
        </p>
      </LegalSection>

      <LegalSection heading="Indemnification">
        <p>
          You agree to indemnify and hold harmless Work &amp; Keep and Wholehearted Wellness LLC from claims, damages,
          and expenses arising out of your use of the Service, Your Content, or your violation of these Terms.
        </p>
      </LegalSection>

      <LegalSection heading="Termination">
        <p>
          You may stop using the Service and cancel at any time as described above. We may suspend or terminate your
          access if you violate these Terms, fail to pay, or use the Service in a way that harms the Service or others.
          Upon termination, your right to use the Service ends. You may export your data before your access period ends.
        </p>
      </LegalSection>

      <LegalSection heading="Changes to the Service and these Terms">
        <p>
          We may update or modify the Service and these Terms from time to time. When we change these Terms, we will
          update the &ldquo;Last updated&rdquo; date above. Your continued use of the Service after changes take effect
          means you accept the updated Terms.
        </p>
      </LegalSection>

      <LegalSection heading="Governing law">
        <p>
          These Terms are governed by the laws of the Commonwealth of Virginia, without regard to conflict-of-law
          principles, except where mandatory consumer-protection or other local laws that apply to you provide
          otherwise. Nothing in these Terms waives any consumer protections that cannot be waived under applicable law.
        </p>
      </LegalSection>

      <LegalSection heading="Contact us">
        <p>
          Questions about these Terms? Contact us at {" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary underline">{SUPPORT_EMAIL}</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
